'use client';

import { useState } from 'react';
import { Send, CheckCircle2, Loader2, Calendar, Phone } from 'lucide-react';
import { trackLeadFormSubmission, trackScheduleMeetingClick, trackPhoneClick, trackFormStart } from '@/lib/analytics';
import { setLeadSubmittedCookie } from '@/lib/utils/lead-cookie';
import { containsNonLatinText, NON_ENGLISH_TEXT_ERROR } from '@/lib/text-validation';

interface MiniContactFormProps {
  /** Service slug for tracking */
  serviceSlug: string;
  /** Service name for display */
  serviceName: string;
  /** Category slug for tracking */
  categorySlug?: string;
  /** Custom title (default: "Get Started with {serviceName}") */
  title?: string;
  /** Custom description */
  description?: string;
  /** Custom button text (default: "Talk to an Expert") */
  buttonText?: string;
  /** Additional className */
  className?: string;
}

interface FormData {
  name: string;
  email: string;
  company: string;
  message: string;
}

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

/**
 * Compact inline contact form for embedding in service pages.
 * Designed to be placed after the hero section to capture leads
 * without requiring navigation to the full contact page.
 */
export default function MiniContactForm({
  serviceSlug,
  serviceName,
  categorySlug,
  title,
  description,
  buttonText = 'Talk to an Expert',
  className = '',
}: MiniContactFormProps) {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [formStarted, setFormStarted] = useState(false);

  const displayTitle = title || `Get Started with ${serviceName}`;
  const displayDescription =
    description ||
    `Fill out this quick form and one of our ${serviceName.toLowerCase()} experts will reach out within 24 hours.`;

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Track form start on first field focus
  const handleFormFocus = () => {
    if (!formStarted) {
      setFormStarted(true);
      trackFormStart('Service Inline Form', serviceSlug);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot check
    if (honeypot) {
      setStatus('success');
      return;
    }

    if (!formData.name.trim()) {
      setErrorMessage('Name is required');
      return;
    }

    if (!formData.email.trim()) {
      setErrorMessage('Email is required');
      return;
    }

    if (!validateEmail(formData.email)) {
      setErrorMessage('Please enter a valid email');
      return;
    }

    // Block non-English (non-Latin script) input in free-text fields
    if (
      containsNonLatinText(formData.name) ||
      containsNonLatinText(formData.company) ||
      containsNonLatinText(formData.message)
    ) {
      setErrorMessage(NON_ENGLISH_TEXT_ERROR);
      return;
    }

    setStatus('submitting');
    setErrorMessage('');

    try {
      const nameParts = formData.name.trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName,
          lastName,
          email: formData.email,
          company: formData.company,
          message: formData.message || `Interested in ${serviceName}`,
          formName: 'Service Page - Inline Contact',
          src: 'service-inline',
          id: serviceSlug,
          cat: categorySlug,
          pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to submit');
      }

      // Track conversion in GA4 and Clarity (includes identification)
      trackLeadFormSubmission('Service Page - Inline Contact', serviceName, undefined, formData.email);

      setStatus('success');

      // Set cookie to suppress exit intent for 7 days
      setLeadSubmittedCookie();
    } catch {
      setStatus('error');
      setErrorMessage('Something went wrong. Please try again.');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Warn immediately if the user types non-English (non-Latin script) characters
    if (name !== 'email' && containsNonLatinText(value)) {
      setErrorMessage(NON_ENGLISH_TEXT_ERROR);
      return;
    }

    if (errorMessage) setErrorMessage('');
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', company: '', message: '' });
    setStatus('idle');
    setErrorMessage('');
  };

  return (
    <section className={`py-12 px-4 ${className}`}>
      <div className="max-w-6xl mx-auto">
        <div className="bg-gradient-to-br from-[#2813e8] to-[#2cc3d1] rounded-2xl shadow-xl overflow-hidden">
          <div className="grid lg:grid-cols-2 gap-0">
            {/* Left Column - Text Content */}
            <div className="p-8 lg:p-10 text-white flex flex-col justify-center">
              <h2 className="text-2xl lg:text-3xl font-bold mb-4">{displayTitle}</h2>
              <p className="text-white/90 mb-6 leading-relaxed">{displayDescription}</p>

              <div className="space-y-4 text-sm">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white/80 flex-shrink-0" />
                  <span>Free consultation, no obligation</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white/80 flex-shrink-0" />
                  <span>Response within 24 hours</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white/80 flex-shrink-0" />
                  <span>Customized solutions for your needs</span>
                </div>
              </div>

              {/* Phone CTA for mobile */}
              <div className="mt-6 lg:mt-8">
                <a
                  href="tel:+18669032097"
                  onClick={() => trackPhoneClick('mini_contact_form')}
                  className="inline-flex items-center gap-2 text-white/90 hover:text-white transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  <span>Or call us: (866) 903-2097</span>
                </a>
              </div>
            </div>

            {/* Right Column - Form */}
            <div className="bg-white p-8 lg:p-10">
              {status === 'success' ? (
                /* Success State */
                <div className="text-center py-6">
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-green-100 rounded-full mb-4">
                    <CheckCircle2 className="w-7 h-7 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Thank You!</h3>
                  <p className="text-gray-600 mb-6">
                    We&apos;ve received your request and will be in touch within 24 hours.
                  </p>

                  <div className="space-y-3">
                    <a
                      href="https://hs.inventivehq.com/meetings/sean1324/contactpage"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackScheduleMeetingClick('mini_contact_form_success')}
                      className="inline-flex items-center justify-center gap-2 w-full py-3 px-4 bg-[#2813e8] hover:bg-[#1f0fb8] text-white font-medium rounded-lg transition-colors"
                    >
                      <Calendar className="w-5 h-5" />
                      Schedule a Call Now
                    </a>
                    <button
                      onClick={resetForm}
                      className="text-[#2813e8] hover:underline text-sm font-medium"
                    >
                      Submit another request
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} name="Service Page - Inline Contact" className="space-y-4">
                  {/* Honeypot */}
                  <div className="absolute -left-[9999px]" aria-hidden="true">
                    <input
                      type="text"
                      name="website"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      aria-label="Leave blank"
                    />
                  </div>

                  {/* Name */}
                  <div>
                    <label htmlFor={`name-${serviceSlug}`} className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id={`name-${serviceSlug}`}
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      onFocus={handleFormFocus}
                      placeholder="John Doe"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2813e8] focus:border-transparent transition-colors"
                      autoComplete="name"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor={`email-${serviceSlug}`} className="block text-sm font-medium text-gray-700 mb-1">
                      Work Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      id={`email-${serviceSlug}`}
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onFocus={handleFormFocus}
                      placeholder="john@company.com"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2813e8] focus:border-transparent transition-colors"
                      autoComplete="email"
                    />
                  </div>

                  {/* Company (optional) */}
                  <div>
                    <label htmlFor={`company-${serviceSlug}`} className="block text-sm font-medium text-gray-700 mb-1">
                      Company <span className="text-gray-400 font-normal">(Optional)</span>
                    </label>
                    <input
                      id={`company-${serviceSlug}`}
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      onFocus={handleFormFocus}
                      placeholder="Acme Inc."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2813e8] focus:border-transparent transition-colors"
                      autoComplete="organization"
                    />
                  </div>

                  {/* Message (optional) */}
                  <div>
                    <label htmlFor={`message-${serviceSlug}`} className="block text-sm font-medium text-gray-700 mb-1">
                      How can we help? <span className="text-gray-400 font-normal">(Optional)</span>
                    </label>
                    <textarea
                      id={`message-${serviceSlug}`}
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      onFocus={handleFormFocus}
                      placeholder="Tell us about your needs..."
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2813e8] focus:border-transparent transition-colors resize-none"
                    />
                  </div>

                  {/* Error message */}
                  {errorMessage && (
                    <p className="text-sm text-red-600">{errorMessage}</p>
                  )}

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="w-full py-3 px-4 bg-[#2813e8] hover:bg-[#1f0fb8] disabled:bg-gray-400 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    {status === 'submitting' ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        {buttonText}
                      </>
                    )}
                  </button>

                  <p className="text-xs text-gray-500 text-center">
                    We respect your privacy. No spam, ever.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
